// Copyright (c) Zammo Inc.
// Standalone ChatGPT-style surface for a single published Zammo agent. Sidebar of past
// conversations + a chat pane; resume any conversation with its full history. Talks to the
// bot's "assistant" channel over the durable watermark log: SSE from watermark 0 replays the
// transcript and then tails live, so resume and live-update are the same mechanism.
//
// Auth: the ?token= access token in the URL gates entry and resolves business + agent server-side.
// The end user is anonymous — identity is a server-signed token the client persists/replays (managed
// by createAssistantClient), never a client-supplied id. Verified per-user identity (JWT embeds) is a
// future mode that binds to the same transport.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import styled, { css } from 'styled-components';
import { useLocation } from 'react-router';
import queryString from 'query-string';

import {
  AssistantAccessRevokedError,
  AssistantActivity,
  AssistantAttachment,
  AssistantChatConfig,
  AssistantClient,
  AssistantConversation,
  AssistantUser,
  authStartUrl,
  createAssistantClient,
} from '../../app/assistant';

import { Markdown } from './Markdown';
import { StatusIndicator } from './StatusIndicator';

/** Compact relative time ("now", "5m", "3h", "2d", "1w") for the conversation list. */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!then || Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 45) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${Math.max(1, m)}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(d / 365)}y`;
}

const STARTERS = ['What can you help me with?', 'Give me a quick overview', 'Show me an example'];

// Inter is the vNext app font (loaded there via Google Fonts). The simulator app ships
// Montserrat, so load Inter on demand for this route to match the agent-designer chat.
function ensureInterFont(): void {
  const ID = 'assistant-inter-font';
  if (document.getElementById(ID)) return;
  const link = document.createElement('link');
  link.id = ID;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const PlusIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const TrashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const PaperclipIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const FileGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

const LogOutIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

const MenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
  >
    <path d="M3 12h18M3 6h18M3 18h18" />
  </svg>
);

// Paper-plane send glyph (matches the agent designer's send button icon).
const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 2 11 13" />
    <path d="m22 2-7 20-4-9-9-4Z" />
  </svg>
);

/* ---------- styles ----------
   Tuned to match the vNext agent-designer chat (next/ZammoNext.Client AgentDesignPage.css):
   right-aligned primary-blue user bubbles, left-aligned white assistant bubbles with a 20px
   radius + 4px chamfer, a dot-pulse "thinking" loader, and slide-in entry. The vNext CSS vars
   are hardcoded here as the simulator app has no access to them:
     --primary hsl(221 83% 53%) · --foreground hsl(224 71% 4%)
     --muted-foreground hsl(220 9% 46%) · --border hsl(220 13% 91%). */

const PRIMARY = 'hsl(245, 80%, 60%)';
const FOREGROUND = 'hsl(224, 71%, 4%)';
const MUTED = 'hsl(220, 9%, 46%)';
const BORDER = 'hsl(220, 13%, 91%)';

const SIDEBAR_PRIMARY = 'hsl(245, 80%, 60%)';
const SIDEBAR_GLOW = 'hsla(245, 80%, 60%, 0.3)';
const DARK_BG = '#09090b'; // Slightly darker obsidian
const DARK_NAV = '#18181b';
const DARK_BORDER = '#27272a';
const DARK_TEXT = '#a1a1aa';
const DARK_TEXT_BRIGHT = '#f4f4f5';

const MOBILE = '@media (max-width: 768px)';

const Layout = styled.div`
  display: flex;
  height: 100vh;
  height: 100dvh; /* avoid the mobile-browser address-bar gap that 100vh leaves */
  width: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: ${FOREGROUND};
  background: #ffffff;
`;

const Sidebar = styled.div<{ $open?: boolean }>`
  width: 280px;
  flex-shrink: 0;
  background: ${DARK_BG};
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 30;
  border-right: 1px solid ${DARK_BORDER};

  /* On phones the sidebar becomes an off-canvas drawer toggled by the header hamburger. */
  ${MOBILE} {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 84vw;
    max-width: 320px;
    transform: translateX(${(p) => (p.$open ? '0' : '-100%')});
    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: ${(p) => (p.$open ? '0 0 40px rgba(0, 0, 0, 0.5)' : 'none')};
  }
`;

// Dimmed overlay behind the open mobile drawer; tap to close. Hidden on desktop.
const Backdrop = styled.div`
  display: none;
  ${MOBILE} {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 20;
    animation: ac-fade-in 0.2s ease;
  }
  @keyframes ac-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Mobile-only top bar with the drawer toggle; hidden on desktop.
const MobileBar = styled.div`
  display: none;
  ${MOBILE} {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid ${BORDER};
    background: #ffffff;
    flex-shrink: 0;
  }
`;

const MobileBarTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${FOREGROUND};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`;

const IconButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  color: ${FOREGROUND};
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover {
    background: hsl(220, 14%, 95%);
  }
`;

const SidebarTop = styled.div`
  padding: 32px 16px 16px;
  display: flex;
  flex-direction: column;
`;

const NewChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 44px;
  border: 1px solid ${DARK_BORDER};
  border-radius: 12px;
  background: ${DARK_NAV};
  color: ${DARK_TEXT_BRIGHT};
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: ${DARK_BORDER};
    border-color: #3f3f46;
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.8;
  }
`;

const SidebarNav = styled.div`
  flex: 1;
  padding: 12px 12px 20px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #27272a;
    border-radius: 10px;
  }
`;

const ListCaption = styled.div`
  padding: 20px 12px 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #71717a;
`;

const ConversationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ConversationRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.2s;
  position: relative;

  ${(p) =>
    p.$active
      ? css`
          background: rgba(255, 255, 255, 0.08);
          color: ${DARK_TEXT_BRIGHT};
        `
      : css`
          color: ${DARK_TEXT};
          &:hover {
            background: rgba(255, 255, 255, 0.04);
            color: ${DARK_TEXT_BRIGHT};
          }
        `}
`;

const RowTitle = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteButton = styled.button`
  opacity: 0;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #52525b;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: opacity 0.15s, color 0.2s;
  &:hover {
    color: #ef4444;
  }
  ${ConversationRow}:hover & {
    opacity: 1;
  }
`;

// Relative time, right-aligned and muted; yields to the delete button on row hover.
const RowTime = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.32);
  white-space: nowrap;
  transition: opacity 0.15s;
  ${ConversationRow}:hover & {
    opacity: 0;
  }
`;

// Inline delete confirmation shown in place of the trash button once armed.
const ConfirmActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  .confirm-label {
    font-size: 11px;
    font-weight: 600;
    color: #ef4444;
    margin-right: 2px;
  }
`;

const ConfirmButton = styled.button<{ $variant?: 'danger' }>`
  border: none;
  background: ${(p) => (p.$variant === 'danger' ? 'rgba(239, 68, 68, 0.15)' : 'transparent')};
  color: ${(p) => (p.$variant === 'danger' ? '#ef4444' : '#a1a1aa')};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s;
  &:hover {
    background: ${(p) => (p.$variant === 'danger' ? 'rgba(239, 68, 68, 0.28)' : '#27272a')};
    color: ${(p) => (p.$variant === 'danger' ? '#ef4444' : '#f4f4f5')};
  }
`;

const SidebarFooter = styled.div`
  padding: 16px;
  border-top: 1px solid ${DARK_BORDER};
`;

// Footer profile: the signed-in user (AAD) or the agent (anonymous). Non-interactive itself; the
// sign-out button inside is the only action.
const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 12px;
`;

const SignOutButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${DARK_TEXT};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${DARK_NAV};
    color: ${DARK_TEXT_BRIGHT};
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${SIDEBAR_PRIMARY}, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 12px;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${DARK_TEXT_BRIGHT};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: 11px;
  color: #71717a;
  font-weight: 500;
`;

const Empty = styled.div`
  font-size: 13px;
  color: #374151;
  padding: 40px 16px;
  text-align: center;
  font-style: italic;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #ffffff;
`;

const Transcript = styled.div`
  flex: 1;
  overflow-y: auto;
  background: #f8fafc;
  /* Don't let the browser re-anchor scroll when the provisional streamed bubble is swapped for the
     final message — that caused a jump up to the last user message and then a scroll back down. We
     drive scrolling explicitly instead. */
  overflow-anchor: none;
`;

const TranscriptInner = styled.div`
  max-width: 768px;
  margin: 0 auto;
  padding: 32px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${MOBILE} {
    padding: 16px 14px 12px;
    gap: 18px;
  }
`;

const Bubble = styled.div<{ $role: string; $noAnim?: boolean }>`
  position: relative;
  max-width: 85%;
  padding: 14px 18px;
  font-size: 14.5px;
  line-height: 1.6;
  border-radius: 20px;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 2px hsl(224 71% 4% / 0.05);
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  /* Skip the slide-in for a streamed bubble — its text already grew in place, so re-animating on
     settle reads as a jerk. */
  animation: ${(p) => (p.$noAnim ? 'none' : 'ac-slide-in 0.25s cubic-bezier(0.16, 1, 0.3, 1)')};
  &:hover {
    transform: scale(1.01);
  }
  ${(p) =>
    p.$role === 'user'
      ? css`
          align-self: flex-end;
          background: ${PRIMARY};
          color: #ffffff;
          border: 1px solid hsla(245, 80%, 60%, 0.3);
          border-bottom-right-radius: 4px;
          box-shadow: 0 10px 25px -5px ${SIDEBAR_GLOW};
        `
      : css`
          align-self: flex-start;
          background: #ffffff;
          color: ${FOREGROUND};
          border: 1px solid ${BORDER};
          border-bottom-left-radius: 4px;
        `}
  @keyframes ac-slide-in {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

// Eyebrow label above a live-agent (human) bubble, distinguishing it from the bot.
const AgentLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${PRIMARY};
  margin-bottom: 4px;
`;

// Centered, muted lifecycle line (agent joined / left, in-queue notices) — not a chat bubble.
const SystemLine = styled.div`
  align-self: center;
  max-width: 80%;
  text-align: center;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${MUTED};
  padding: 4px 12px;
  margin: 2px 0;
`;

const Composer = styled.div`
  padding: 20px 32px 28px;
  ${MOBILE} {
    padding: 10px 12px max(14px, env(safe-area-inset-bottom));
  }
`;

const ComposerInner = styled.div`
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid hsl(220, 13%, 88%);
  border-radius: 16px;
  padding: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px hsl(224 71% 4% / 0.05);
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus-within {
    border-color: ${PRIMARY};
    box-shadow: 0 0 0 3px ${SIDEBAR_GLOW};
  }
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
`;

const TextArea = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.5;
  max-height: 200px;
  padding: 8px 4px;
  color: ${FOREGROUND};
  background: transparent;
  &::placeholder {
    color: ${MUTED};
  }
  ${MOBILE} {
    font-size: 16px; /* >=16px stops iOS Safari from zooming in on focus */
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 14px;
  background: ${PRIMARY};
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 12px ${SIDEBAR_GLOW};
  transition: filter 0.15s, background 0.15s;
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  &:disabled {
    background: #eef2ff;
    color: #c7d2fe;
    box-shadow: none;
    cursor: default;
  }
`;

const Hint = styled.div`
  max-width: 768px;
  margin: 8px auto 0;
  text-align: center;
  font-size: 11px;
  color: ${MUTED};
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid ${MUTED};
  border-top-color: transparent;
  opacity: 0.5;
  animation: ac-spin 0.7s linear infinite;
  @keyframes ac-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Welcome = styled.div`
  width: 100%;
  max-width: 720px;
  padding: 0 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ac-slide-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  h2 {
    margin: 0;
    font-size: 40px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: hsl(224, 10%, 10%);
    line-height: 1.2;
  }
  p {
    margin: 12px 0 0;
    font-size: 17px;
    color: hsl(220, 9%, 55%);
    font-weight: 400;
  }
  ${MOBILE} {
    padding: 0 20px;
    h2 {
      font-size: 30px;
    }
    p {
      font-size: 15.5px;
      margin-top: 12px;
    }
  }
`;

const Chips = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 32px;
  width: 100%;
  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const Chip = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  border: 1px solid hsl(220, 13%, 94%);
  border-radius: 16px;
  background: hsl(220, 20%, 99%);
  color: ${FOREGROUND};
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.35;
  padding: 13px 16px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s;
  &:hover {
    background: #ffffff;
    border-color: ${PRIMARY};
    color: ${PRIMARY};
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
  }
  &:active {
    transform: translateY(0);
  }
`;

// Microsoft sign-in button (gate shown when the channel requires AAD login).
const SignInButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding: 12px 22px;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  background: #ffffff;
  color: ${FOREGROUND};
  font: inherit;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
  &:hover {
    background: #f8fafc;
    box-shadow: 0 4px 10px -2px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
`;

// Microsoft four-square logo.
const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const ErrorBox = styled.div`
  max-width: 520px;
  padding: 16px 20px;
  border: 1px solid #f3c2bd;
  background: #fdf3f2;
  color: #b42318;
  border-radius: 10px;
  font-size: 14px;
`;

// Dot-pulse "thinking" loader — matches the agent designer's .dot-loader.
const DotLoader = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${MUTED};
    opacity: 0.4;
    animation: ac-dot-pulse 1.4s infinite ease-in-out both;
  }
  span:nth-child(1) {
    animation-delay: -0.32s;
  }
  span:nth-child(2) {
    animation-delay: -0.16s;
  }
  @keyframes ac-dot-pulse {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
      opacity: 0.8;
    }
  }
`;

const AttachButton = styled.button`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: ${MUTED};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover:not(:disabled) {
    background: hsl(220, 14%, 95%);
    color: ${FOREGROUND};
  }
  &:disabled {
    color: hsl(220, 13%, 80%);
    cursor: default;
  }
`;

// Pending-attachment chips, shown above the textarea before send.
const AttachBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-bottom: 8px;
`;

const PendingChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 220px;
  padding: 6px 8px 6px 6px;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  background: hsl(220, 20%, 98%);
  font-size: 12px;
  color: ${FOREGROUND};
`;

const PendingThumb = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

const PendingFileIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: hsl(221 83% 53% / 0.1);
  color: ${PRIMARY};
`;

const PendingName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
`;

const ChipRemove = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: ${MUTED};
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  &:hover {
    color: #d92d20;
  }
`;

// Attachments rendered inside a message bubble.
const BubbleAttachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const BubbleThumb = styled.img`
  max-width: 240px;
  max-height: 240px;
  border-radius: 10px;
  object-fit: cover;
  display: block;
  cursor: pointer;
`;

const BubbleFileChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  color: inherit;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.25);
`;

const BubbleAttachmentList = ({ items }: { items: AssistantAttachment[] }) => {
  if (!items || items.length === 0) return null;
  return (
    <BubbleAttachments>
      {items.map((a, i) =>
        a.isImage ? (
          <a key={i} href={a.url} target="_blank" rel="noopener noreferrer">
            <BubbleThumb src={a.url} alt={a.name} />
          </a>
        ) : (
          <BubbleFileChip key={i} href={a.url} target="_blank" rel="noopener noreferrer">
            <FileGlyph />
            {a.name}
          </BubbleFileChip>
        )
      )}
    </BubbleAttachments>
  );
};

const AssistantChat: React.FC = () => {
  const location = useLocation();
  const params = useMemo(() => queryString.parse(location.search), [location.search]);
  const accessToken = `${params.token ?? ''}`;

  // The session-managing client (mints lazily, re-mints + retries on 401). `ready` flips once the
  // first session is minted, gating the composer; the client itself survives session drops.
  const clientRef = useRef<AssistantClient | null>(null);
  const [ready, setReady] = useState(false);
  // Display name of the agent the visitor is talking to (from the session mint); null = use a fallback label.
  const [agentName, setAgentName] = useState<string | null>(null);
  // Chat-surface customization (display name, icon, welcome text, starters) set on the Channels page.
  const [config, setConfig] = useState<AssistantChatConfig | null>(null);
  // The signed-in user (AAD channels only); null for anonymous visitors.
  const [user, setUser] = useState<AssistantUser | null>(null);
  // Conversation id whose delete is awaiting inline confirmation (null = none armed).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Mobile only: whether the off-canvas sidebar drawer is open.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fatal, setFatal] = useState<string>('');
  // Non-null when the channel requires sign-in (e.g. "aad") and the visitor isn't authenticated yet.
  const [authRequired, setAuthRequired] = useState<string | null>(null);
  const [conversations, setConversations] = useState<AssistantConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantActivity[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [awaitingReply, setAwaitingReply] = useState(false);
  // The live agent is actively typing (transient SSE 'typing' signal during handoff).
  const [agentTyping, setAgentTyping] = useState(false);
  // Optimistic echo of the just-sent user message so it renders immediately (above the typing
  // indicator) instead of only once it round-trips back over SSE. Cleared when the real
  // user activity lands, on conversation switch / new chat, or on send error.
  const [pendingUserText, setPendingUserText] = useState<string | null>(null);
  const [pendingUserAttachments, setPendingUserAttachments] = useState<AssistantAttachment[]>([]);
  // Files uploaded for the next send, shown as chips above the composer.
  const [attachments, setAttachments] = useState<AssistantAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  // Provisional bot bubble assembled from SSE token deltas during a streaming send. Cleared once the
  // final message is reconciled into `messages` (by watermark) on the stream's `done` frame.
  const [streamingText, setStreamingText] = useState<string | null>(null);
  // Ephemeral pre-tool status ("Checking the logs…") shown on its own line, separate from the
  // answer bubble; set by the SSE `status` frame, cleared once real answer tokens arrive.
  const [statusText, setStatusText] = useState<string | null>(null);

  // Disposer for the active conversation's SSE tail (the client owns the underlying EventSource).
  const closeStreamRef = useRef<(() => void) | null>(null);
  const seenRef = useRef<Set<number>>(new Set());
  // Watermarks of just-streamed replies — rendered without the slide-in so the settle is seamless.
  const noAnimRef = useRef<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Whether the conversation is currently handed off to a human agent (from the send response).
  // Mid-handoff we don't auto-show the typing dots — the agent replies on their own schedule.
  const inHandoffRef = useRef(false);
  // Auto-clears the agent-typing dots a few seconds after the last 'typing' pulse.
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const grouped = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const groups: { [key: string]: AssistantConversation[] } = {
      Today: [],
      Yesterday: [],
      'Previous 7 Days': [],
      Older: [],
    };

    conversations.forEach((c) => {
      const date = new Date(c.updatedAt);
      if (date >= today) groups.Today.push(c);
      else if (date >= yesterday) groups.Yesterday.push(c);
      else if (date >= sevenDaysAgo) groups['Previous 7 Days'].push(c);
      else groups.Older.push(c);
    });

    return Object.entries(groups).filter(([, items]) => items.length > 0);
  }, [conversations]);

  useEffect(() => {
    ensureInterFont();
  }, []);

  // --- bootstrap: build the client, mint a session, load the conversation list ---
  useEffect(() => {
    let cancelled = false;
    if (!accessToken) {
      setFatal('This chat link is missing its access token.');
      return;
    }
    const client = createAssistantClient({ token: accessToken });
    clientRef.current = client;

    // Returning from a Microsoft sign-in: the callback redirects to /chat#id=<identity> (or
    // #autherror=<code>). Adopt the identity / surface the error, then scrub the fragment so the
    // token isn't left in the address bar or history.
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const hashIdentity = hash.get('id');
    const hashError = hash.get('autherror');
    if (hashIdentity || hashError) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (hashIdentity) client.adoptIdentity(hashIdentity);
    if (hashError) {
      setFatal(
        hashError === 'domain'
          ? 'Your account isn’t allowed to use this chat. Check with whoever shared the link.'
          : 'Sign-in didn’t complete. Please try again.'
      );
      return;
    }

    (async () => {
      try {
        const { authRequired } = await client.start();
        if (cancelled) return;
        if (authRequired) {
          setAuthRequired(authRequired);
          return;
        }
        setReady(true);
        setAgentName(client.agentName());
        setConfig(client.config());
        setUser(client.user());
        const list = await client.listConversations();
        if (cancelled) return;
        setConversations(list);
        if (list.length > 0) setActiveId(list[0].id);
      } catch (e) {
        if (cancelled) return;
        // Only call it "invalid/expired" when the URL token was actually rejected — other failures
        // (bot restarting, network blip) are transient and a reload usually fixes them.
        setFatal(
          e instanceof AssistantAccessRevokedError
            ? 'This chat link is invalid or has expired.'
            : 'Couldn’t reach the assistant. Please refresh to try again.'
        );
      }
    })();
    return () => {
      cancelled = true;
      clientRef.current = null;
    };
  }, [accessToken]);

  const refreshList = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return;
    try {
      setConversations(await client.listConversations());
    } catch {
      /* non-fatal */
    }
  }, []);

  // --- open an SSE tail for the active conversation (replays from 0, then live) ---
  useEffect(() => {
    closeStreamRef.current?.();
    closeStreamRef.current = null;
    seenRef.current = new Set();
    noAnimRef.current = new Set();
    setMessages([]);
    setAgentTyping(false);
    setStreamingText(null);
    setStatusText(null);
    const client = clientRef.current;
    if (!ready || !client || !activeId) return;

    // rAF-batched streaming: the runtime emits one SSE `token` event per model token (often a few
    // chars). Calling setStreamingText per token would force a full transcript re-render + a markdown
    // re-parse of the whole growing reply EACH token (O(n²), and un-batched in React 17 native event
    // handlers). Instead we accumulate deltas in a buffer and flush to state once per animation frame,
    // collapsing hundreds of renders into ~60/s with no perceptible latency. `streamBuf` is the source
    // of truth for the in-flight draft; `cancelStream` drops it when a settled message supersedes it.
    let streamBuf: string | null = null;
    let rafId: number | null = null;
    const flush = () => {
      rafId = null;
      if (streamBuf !== null) setStreamingText(streamBuf);
    };
    const scheduleFlush = () => {
      if (rafId === null) rafId = requestAnimationFrame(flush);
    };
    const cancelStream = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      streamBuf = null;
    };

    // The client owns the EventSource and transparently re-mints the session + reconnects if the tail
    // is dropped (incl. a 401 after a bot restart), so an idle tab keeps tailing without a reload.
    closeStreamRef.current = client.openStream(activeId, 0, {
      onActivity: (activity) => {
        if (seenRef.current.has(activity.watermark)) return;
        seenRef.current.add(activity.watermark);
        const isUserMsg = activity.role === 'user' && activity.type === 'message';
        const isOtherMsg = activity.role !== 'user' && activity.type === 'message';
        if (isUserMsg) {
          // This real activity replaces the optimistic echo — render it without the slide-in so the
          // swap is seamless (otherwise it re-animates opacity 0→1 and looks like a flicker).
          noAnimRef.current.add(activity.watermark);
        }
        // Batch into one render (React 17 legacy doesn't auto-batch native handlers) so the bubble
        // swap doesn't briefly shrink the transcript and jerk the scroll.
        unstable_batchedUpdates(() => {
          setMessages((prev) => [...prev, activity]);
          if (isUserMsg) {
            setPendingUserText(null);
            setPendingUserAttachments([]);
          }
          // Any inbound non-user message (bot, or a live agent / system line during handoff) ends the
          // "waiting" state — otherwise the typing dots hang forever when the LLM is skipped mid-handoff.
          if (isOtherMsg) {
            setAwaitingReply(false);
            setAgentTyping(false); // the message itself supersedes the typing pulse
            setStatusText(null); // the settled reply supersedes any ephemeral "what I'm doing" status
            cancelStream(); // drop any buffered draft so a late flush can't resurrect it
            setStreamingText(null); // the settled activity replaces the provisional streamed bubble
          }
        });
      },
      // Live token deltas from a streaming turn — fill a provisional bot bubble until the settled
      // activity above arrives. Delivered over EventSource (not the POST body) so Safari streams them.
      // Accumulate into streamBuf and flush on the next frame rather than re-rendering per token.
      onToken: (delta) => {
        setAwaitingReply(false);
        setStatusText(null); // real answer tokens supersede the ephemeral status
        streamBuf = (streamBuf ?? '') + delta;
        scheduleFlush();
      },
      // A tool call superseded the streamed draft — drop the provisional text. The ephemeral status
      // (set just before this) intentionally survives: it's what we show while the tool runs.
      onReset: () => {
        cancelStream();
        setStreamingText(null);
      },
      // Ephemeral "what I'm about to do" note — shown on its own line above the bubble while a tool
      // runs, NOT concatenated into the answer. Cleared by onToken/onFinal/onActivity above.
      onStatus: (text) => {
        setStatusText(text);
        setAwaitingReply(false);
      },
      // Settled reply (pushed right after the tokens): swap the provisional bubble for the final
      // message immediately, without re-animating it, and dedup against the durable-log copy.
      onFinal: ({ watermark, text }) => {
        const isNew = !!watermark && !seenRef.current.has(watermark);
        if (isNew) {
          seenRef.current.add(watermark);
          noAnimRef.current.add(watermark);
        }
        // Batch the swap into ONE render (React 17 + legacy ReactDOM.render doesn't auto-batch native
        // event handlers): clearing the provisional bubble and appending the final message in separate
        // renders briefly shrinks the transcript, which jerks the scroll up then back down.
        unstable_batchedUpdates(() => {
          setAwaitingReply(false);
          setStatusText(null); // the settled answer supersedes any ephemeral status
          if (isNew) {
            setMessages((prev) => [
              ...prev,
              { watermark, role: 'bot', type: 'message', text, createdAt: new Date().toISOString() },
            ]);
          }
          cancelStream(); // a pending flush would otherwise re-show the provisional bubble after the swap
          setStreamingText(null);
        });
      },
      // Transient "live agent is typing" pulse (never a logged activity) — show dots, and auto-clear
      // shortly after the pulses stop.
      onTyping: () => {
        setAgentTyping(true);
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setAgentTyping(false), 4000);
      },
    });
    return () => {
      closeStreamRef.current?.();
      closeStreamRef.current = null;
      cancelStream(); // cancel any pending rAF flush so it can't fire after teardown / conv switch
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [ready, activeId]);

  // --- keep the transcript pinned to the bottom ---
  // Follow the growing streamed text instantly (smooth-scrolling on every token competes with itself
  // and stutters); use a smooth glide only for discrete message/indicator changes.
  // useLayoutEffect (not useEffect): the pin must run after the taller content is laid out but BEFORE
  // paint, otherwise the browser paints one frame with the new last line below the fold (overflowing
  // the container) before a passive effect catches up — a visible jump as each streamed line lands.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: streamingText !== null ? 'auto' : 'smooth' });
  }, [messages, awaitingReply, agentTyping, streamingText, statusText]);

  const onNewChat = useCallback(() => {
    setActiveId(null);
    setMessages([]);
    setPendingUserText(null);
    setPendingUserAttachments([]);
    setAttachments([]);
    setAwaitingReply(false);
    setStreamingText(null);
    inHandoffRef.current = false;
    seenRef.current = new Set();
    noAnimRef.current = new Set();
    setSidebarOpen(false); // close the mobile drawer
  }, []);

  // Resolve the active conversation, creating one if this is a fresh chat.
  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (activeId) return activeId;
    const client = clientRef.current;
    if (!client) return null;
    const conv = await client.createConversation();
    setActiveId(conv.id); // opens the stream for this conversation
    await refreshList();
    return conv.id;
  }, [activeId, refreshList]);

  const onPickFiles = useCallback(
    async (files: FileList | null) => {
      const client = clientRef.current;
      if (!files || files.length === 0 || !client) return;
      setUploading(true);
      try {
        const cid = await ensureConversation();
        if (!cid) return;
        const uploaded = await client.uploadAttachments(cid, Array.from(files));
        setAttachments((prev) => [...prev, ...uploaded]);
      } catch (e) {
        setFatal((e as Error).message);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [ensureConversation]
  );

  const onSend = useCallback(
    async (override?: string) => {
      const text = (override ?? input).trim();
      const client = clientRef.current;
      if ((!text && attachments.length === 0) || !client || sending) return;
      const sent = attachments;
      setSending(true);
      setInput('');
      setAttachments([]);
      setPendingUserText(text.length > 0 ? text : null);
      setPendingUserAttachments(sent);
      try {
        const cid = await ensureConversation();
        if (!cid) throw new Error('Could not open a conversation.');
        // Don't show the "assistant is typing" dots if we already know we're handed off to a human
        // agent — they reply on their own schedule, so it would hang. The send response confirms the
        // current handoff state (it can flip when the agent joins/leaves).
        setAwaitingReply(!inHandoffRef.current);
        // Trigger the streaming turn. Token deltas + the final activity arrive over the SSE tail
        // (token/reset/activity events) — this call just resolves with the settled handoff state.
        const done = await client.streamMessage(cid, text, sent);
        inHandoffRef.current = done.inHandoff;
        if (done.inHandoff) {
          setAwaitingReply(false);
          setStreamingText(null);
        }
        refreshList(); // title/updatedAt may have changed
        // The concise first-turn title is generated server-side a beat after the reply lands, so
        // re-fetch shortly after to swap the verbatim seed title for it.
        window.setTimeout(() => refreshList(), 2500);
      } catch (e) {
        setFatal((e as Error).message);
        setAwaitingReply(false);
        setStreamingText(null);
        setPendingUserText(null);
        setPendingUserAttachments([]);
      } finally {
        setSending(false);
      }
    },
    [input, sending, attachments, ensureConversation, refreshList]
  );

  const onDelete = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      const client = clientRef.current;
      if (!client) return;
      setConfirmDeleteId(null);
      // Optimistically drop it from the sidebar so the UI feels instant; refreshList reconciles after.
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) onNewChat();
      try {
        await client.deleteConversation(id);
      } catch {
        /* non-fatal — refreshList will restore it if the server didn't actually delete it */
      }
      await refreshList();
    },
    [activeId, onNewChat, refreshList]
  );

  const visible = messages.filter(
    (m) => m.type === 'message' && ((m.text ?? '').length > 0 || (m.attachments?.length ?? 0) > 0)
  );
  // Show the optimistic echo until the real user activity lands (becomes the last bubble).
  const lastVisible = visible[visible.length - 1];
  const hasPending = pendingUserText !== null || pendingUserAttachments.length > 0;
  const showPendingUser = hasPending && lastVisible?.role !== 'user';

  // True until the first session mint resolves (success, sign-in-required, or fatal). Until then we
  // show a neutral loader instead of the chat surface, so an AAD-gated link doesn't flash the chat
  // UI before the "Sign in to continue" gate appears.
  const booting = !ready && !authRequired && !fatal;

  // Channels-page customization with sensible fallbacks.
  const displayName = config?.displayName?.trim() || agentName || 'Assistant';
  const iconUrl = config?.iconUrl?.trim() || null;
  const welcomeTitle = config?.welcomeTitle?.trim() || 'How can I help?';
  const welcomeSubtitle = config?.welcomeSubtitle?.trim() || 'Ask me anything to get started.';
  const starters = config?.starters && config.starters.length > 0 ? config.starters : STARTERS;

  // When signed in (AAD channels), the footer shows the user; otherwise it shows the agent.
  const userLabel = user ? user.name?.trim() || user.email?.trim() || 'Signed in' : null;
  const footerTitle = userLabel ?? displayName;
  const footerSubtitle = user ? user.email?.trim() || 'Signed in' : 'AI Assistant';
  const footerAvatar = user ? null : iconUrl; // user shows an initial; agent can show its uploaded icon
  const footerInitial = (footerTitle.charAt(0) || 'A').toUpperCase();

  return (
    <Layout>
      {/* Hide the whole rail while booting, when the visitor is gated out (sign-in required), or in a
          fatal/error state — the loader / gate / error should be a clean full-page screen, not framed
          by an empty sidebar. */}
      {!booting && !authRequired && !fatal && (
        <Sidebar $open={sidebarOpen}>
          <SidebarTop>
            <NewChatButton onClick={onNewChat}>
              <PlusIcon />
              New chat
            </NewChatButton>
          </SidebarTop>

          <SidebarNav>
            {grouped.map(([label, items]) => (
              <React.Fragment key={label}>
                <ListCaption>{label}</ListCaption>
                <ConversationList>
                  {items.map((c) => (
                    <ConversationRow
                      key={c.id}
                      $active={c.id === activeId}
                      onClick={() => {
                        setConfirmDeleteId(null);
                        setActiveId(c.id);
                        setPendingUserText(null);
                        setPendingUserAttachments([]);
                        setAttachments([]);
                        setAwaitingReply(false);
                        inHandoffRef.current = false;
                        setSidebarOpen(false); // close the mobile drawer
                      }}
                    >
                      <RowTitle>{c.title || 'New conversation'}</RowTitle>
                      {confirmDeleteId === c.id ? (
                        <ConfirmActions onClick={(e) => e.stopPropagation()}>
                          <span className="confirm-label">Delete?</span>
                          <ConfirmButton title="Confirm delete" $variant="danger" onClick={(e) => onDelete(e, c.id)}>
                            <CheckIcon />
                          </ConfirmButton>
                          <ConfirmButton
                            title="Cancel"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(null);
                            }}
                          >
                            <CloseIcon />
                          </ConfirmButton>
                        </ConfirmActions>
                      ) : (
                        <>
                          <RowTime>{timeAgo(c.updatedAt)}</RowTime>
                          <DeleteButton
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(c.id);
                            }}
                          >
                            <TrashIcon />
                          </DeleteButton>
                        </>
                      )}
                    </ConversationRow>
                  ))}
                </ConversationList>
              </React.Fragment>
            ))}
            {conversations.length === 0 && <Empty>No conversations yet.</Empty>}
          </SidebarNav>

          <SidebarFooter>
            <UserProfile>
              <UserAvatar>
                {footerAvatar ? (
                  <img
                    src={footerAvatar}
                    alt=""
                    style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }}
                  />
                ) : (
                  footerInitial
                )}
              </UserAvatar>
              <UserInfo>
                <UserName>{footerTitle}</UserName>
                <UserRole>{footerSubtitle}</UserRole>
              </UserInfo>
              {user && (
                <SignOutButton
                  title="Sign out"
                  onClick={() => {
                    clientRef.current?.signOut();
                    window.location.reload();
                  }}
                >
                  <LogOutIcon />
                </SignOutButton>
              )}
            </UserProfile>
          </SidebarFooter>
        </Sidebar>
      )}
      {!booting && !authRequired && !fatal && sidebarOpen && <Backdrop onClick={() => setSidebarOpen(false)} />}

      <Main>
        {!booting && !authRequired && !fatal && (
          <MobileBar>
            <IconButton title="Menu" onClick={() => setSidebarOpen(true)}>
              <MenuIcon />
            </IconButton>
            <MobileBarTitle>{displayName}</MobileBarTitle>
            <IconButton title="New chat" onClick={onNewChat}>
              <PlusIcon />
            </IconButton>
          </MobileBar>
        )}
        {fatal ? (
          <Centered>
            <ErrorBox>{fatal}</ErrorBox>
          </Centered>
        ) : booting ? (
          <Centered>
            <Spinner />
          </Centered>
        ) : authRequired ? (
          <Centered>
            <Welcome>
              <h2>Sign in to continue</h2>
              <p>This chat requires you to sign in with your work account.</p>
              <SignInButton onClick={() => (window.location.href = authStartUrl(accessToken))}>
                <MicrosoftLogo />
                Continue with Microsoft
              </SignInButton>
            </Welcome>
          </Centered>
        ) : visible.length === 0 && !awaitingReply && !showPendingUser ? (
          <Centered>
            <Welcome>
              <h2>{welcomeTitle}</h2>
              <p>{welcomeSubtitle}</p>
              <Chips>
                {starters.map((s) => (
                  <Chip key={s} onClick={() => onSend(s)} disabled={!ready}>
                    <span>{s}</span>
                  </Chip>
                ))}
              </Chips>
            </Welcome>
          </Centered>
        ) : (
          <Transcript ref={scrollRef}>
            <TranscriptInner>
              {visible.map((m) =>
                m.role === 'system' ? (
                  <SystemLine key={m.watermark}>{m.text}</SystemLine>
                ) : (
                  <Bubble key={m.watermark} $role={m.role} $noAnim={noAnimRef.current.has(m.watermark)}>
                    {m.role === 'agent' && <AgentLabel>Agent</AgentLabel>}
                    {m.attachments && m.attachments.length > 0 && <BubbleAttachmentList items={m.attachments} />}
                    {m.role === 'bot' || m.role === 'agent' ? <Markdown text={m.text ?? ''} /> : m.text}
                  </Bubble>
                )
              )}
              {showPendingUser && (
                <Bubble $role="user">
                  {pendingUserAttachments.length > 0 && <BubbleAttachmentList items={pendingUserAttachments} />}
                  {pendingUserText}
                </Bubble>
              )}
              {/* Ephemeral "what I'm about to do" status — a transient, borderless activity line
                  shown while a tool runs, replaced by the answer bubble once tokens arrive. */}
              {statusText !== null && streamingText === null && <StatusIndicator text={statusText} />}
              {streamingText !== null && (
                <Bubble $role="bot" $noAnim>
                  <Markdown text={streamingText} />
                </Bubble>
              )}
              {streamingText === null && statusText === null && (awaitingReply || agentTyping) && (
                <Bubble $role={agentTyping ? 'agent' : 'bot'}>
                  {agentTyping && <AgentLabel>Agent</AgentLabel>}
                  <DotLoader>
                    <span />
                    <span />
                    <span />
                  </DotLoader>
                </Bubble>
              )}
            </TranscriptInner>
          </Transcript>
        )}

        {!booting && !authRequired && !fatal && (
          <Composer>
            <ComposerInner>
              {attachments.length > 0 && (
                <AttachBar>
                  {attachments.map((a, i) => (
                    <PendingChip key={i}>
                      {a.isImage ? (
                        <PendingThumb src={a.url} alt={a.name} />
                      ) : (
                        <PendingFileIcon>
                          <FileGlyph />
                        </PendingFileIcon>
                      )}
                      <PendingName>{a.name}</PendingName>
                      <ChipRemove
                        title="Remove"
                        onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        ×
                      </ChipRemove>
                    </PendingChip>
                  ))}
                </AttachBar>
              )}
              <ComposerRow>
                <AttachButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !ready || !!fatal}
                  title={uploading ? 'Uploading…' : 'Attach files'}
                >
                  <PaperclipIcon />
                </AttachButton>
                <TextArea
                  value={input}
                  placeholder="Message the assistant…"
                  disabled={!ready || !!fatal}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                  rows={1}
                />
                <SendButton
                  onClick={() => onSend()}
                  disabled={(!input.trim() && attachments.length === 0) || sending || uploading || !ready}
                  title="Send"
                >
                  <SendIcon />
                </SendButton>
              </ComposerRow>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => onPickFiles(e.target.files)}
              />
            </ComposerInner>
            <Hint>
              <strong>Enter</strong> to send · <strong>Shift + Enter</strong> for newline
            </Hint>
          </Composer>
        )}
      </Main>
    </Layout>
  );
};

export default AssistantChat;
